import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterNextRender,
  computed,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';

import { MenuItem } from '../../../core/menu/domain/menu-item.model';
import { joinRoutePaths } from '../../../shared/utils/route-path.util';
import { moduleIconPath } from './sidebar-icons';
import { SidebarNavItemComponent } from './sidebar-nav-item.component';
import { SidebarUser } from './sidebar-user.model';

export type SidebarMode = 'inline' | 'overlay';

/** Elementos que reciben foco con Tab dentro del panel (focus trap del modo overlay). */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SidebarNavItemComponent],
  host: { '(document:keydown.escape)': 'onEscape()' },
})
export class SidebarComponent {
  items = input<MenuItem[]>([]);
  collapsed = input(false);
  activeUrl = input('');
  /** Usuario activo para el footer de sesión. `null` = no renderiza el footer. */
  user = input<SidebarUser | null>(null);
  /** Término del buscador. Lo mantiene el shell para que sobreviva si el sidebar se vuelve a crear. */
  searchTerm = input('');
  /**
   * `inline`: columna en línea con el contenido (mediana y grande).
   * `overlay`: panel superpuesto de 256 px con fondo oscuro (móvil). En este
   * modo siempre se muestra expandido y el botón de cabecera pide cerrar.
   */
  mode = input<SidebarMode>('inline');

  toggleCollapse = output<void>();
  searchTermChange = output<string>();
  logout = output<void>();
  /** Solo en modo overlay: fondo, `Escape` o botón de cabecera. */
  closeRequest = output<void>();

  protected readonly expandedIds = signal<Set<string>>(new Set());

  protected readonly isOverlay = computed(() => this.mode() === 'overlay');
  protected readonly isCollapsed = computed(() => !this.isOverlay() && this.collapsed());

  protected readonly panelClasses = computed(() => {
    if (this.isOverlay()) return 'fixed inset-y-0 left-0 z-50 w-64 max-w-full shadow-lg';
    return `h-full shrink-0 transition-all ${this.isCollapsed() ? 'w-16' : 'w-64'}`;
  });

  protected readonly toggleLabel = computed(() => {
    if (this.isOverlay()) return 'Cerrar menú';
    return this.isCollapsed() ? 'Expandir menú' : 'Colapsar menú';
  });

  private readonly panel = viewChild.required<ElementRef<HTMLElement>>('panel');

  constructor() {
    // Al abrirse como panel móvil, el foco entra al diálogo (accesibilidad).
    afterNextRender(() => {
      if (this.isOverlay()) this.panel().nativeElement.focus();
    });
  }

  private readonly activeModuleIds = computed(() => {
    const url = this.activeUrl();
    const ids = new Set<string>();

    for (const item of this.items()) {
      const hasActiveChild = item.subModules?.some((child) =>
        this.isRouteActive(url, this.childRoute(item, child)),
      );
      if (hasActiveChild) ids.add(item.id);
    }

    return ids;
  });

  protected isExpanded(id: string): boolean {
    return this.expandedIds().has(id) || this.activeModuleIds().has(id);
  }

  protected isModuleActive(item: MenuItem): boolean {
    return this.activeModuleIds().has(item.id);
  }

  protected toggleExpand(id: string): void {
    const next = new Set(this.expandedIds());
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    this.expandedIds.set(next);
  }

  protected childRoute(module: MenuItem, child: MenuItem): string {
    return joinRoutePaths(module.route, child.route);
  }

  protected iconPath(item: MenuItem): string {
    return moduleIconPath(item);
  }

  protected onSearchInputChange(event: Event): void {
    this.searchTermChange.emit((event.target as HTMLInputElement).value);
  }

  protected onToggleClick(): void {
    if (this.isOverlay()) {
      this.closeRequest.emit();
    } else {
      this.toggleCollapse.emit();
    }
  }

  /**
   * Focus trap del modo overlay (`role="dialog"` + `aria-modal`): Tab y
   * Shift+Tab ciclan entre los elementos enfocables del panel sin salir de él.
   */
  protected onPanelKeydown(event: KeyboardEvent): void {
    if (!this.isOverlay() || event.key !== 'Tab') return;

    const panel = this.panel().nativeElement;
    const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
      (el) => el.getClientRects().length > 0,
    );
    if (focusables.length === 0) {
      event.preventDefault();
      panel.focus();
      return;
    }

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    const outside = !active || !focusables.includes(active);

    if (event.shiftKey && (outside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (outside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  }

  protected onEscape(): void {
    if (this.isOverlay()) this.closeRequest.emit();
  }

  private isRouteActive(url: string, route: string): boolean {
    // activeUrl llega con "/" inicial (y posible query/fragment); las rutas del menú no.
    const path = url.split(/[?#]/)[0].replace(/^\/+/, '');
    const target = route.replace(/^\/+/, '');
    return path === target || path.startsWith(`${target}/`);
  }
}
